import { Fragment, useEffect, useRef, useState } from 'react';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import FeaturedPost from 'src/modules/community/shared/components/featuredPosts/featuredPost';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFeaturedPosts } from 'src/modules/shared/context/actions';
import { formatFeaturedPostsForSinglePost } from 'src/utils/featuredPosts';
import { Featured, State } from 'src/types';
import { hideFloatingSignupBox } from 'src/modules/shared/context/actions';

export default function FeaturedPosts() {
  const featuredPostFooter = useRef<HTMLDivElement>(null);

  const featuredPosts = useFetchFeaturedPosts();
  const HUStickyCampaign = useHUStickyCampaign();

  const skipFeaturedPosts = () => {
    featuredPostFooter.current?.scrollIntoView(true);
    trackFootfallEvent(footfallEventTypes.SKIPPED_FEATURED_POST);
  };

  return (
    <Fragment>
      {featuredPosts?.length > 0 &&
        <div className="featured-post__box">
          <div className="featured-post__header">
            <p>{i18n.t('Featured Content')}</p>
            <button className="featured-post__skip" onClick={skipFeaturedPosts}>
              {i18n.t('Jump to replies')}
            </button>
          </div>
          <div className="featured-post__list">
            {featuredPosts.map((featuredPost) =>
              <FeaturedPost featuredPost={featuredPost} key={featuredPost.featuredPostId} />,
            )}
          </div>
          <div ref={featuredPostFooter} />
        </div>
      }
      {HUStickyCampaign && <FeaturedPost featuredPost={HUStickyCampaign} isSticky={true} />}
    </Fragment>
  );
}

const useFetchFeaturedPosts = () => {
  const dispatch = useDispatch();
  const { post } = useSelector(({ pages }: State) => pages.singlePost);
  const { currentUser, currentCommunity, lang } = useSelector(({ context }: State) => context);

  useEffect(() => {
    if (currentCommunity && post) {
      dispatch(fetchFeaturedPosts({
        communityId: currentCommunity.communityId,
        countryId: 'all',
        language: lang || 'en',
        postId: post.id,
        userType: currentUser?.userId ? 'member' : 'visitor',
      }));
    }
  }, [currentCommunity?.communityId, lang, currentUser?.userId, post?.id]);

  return useFormattedFeaturedPosts();
};

const useFormattedFeaturedPosts = () => {
  const featuredPosts = useSelector(({ context }: State) => context.currentCommunity?.featuredPosts);
  const dismissedFeaturedPostIds = useSelector(({ context }: State) => context.currentCommunity?.dismissedFeaturedPostIds) || [];

  const [formattedFeaturedPosts, setFormattedFeaturedPosts] = useState<Featured[]>([]);

  useEffect(() => {
    if (!featuredPosts || featuredPosts.length === 0) return;
    const filteredFeaturedPosts = featuredPosts.filter(({ featuredPostId }: Featured) =>
      !dismissedFeaturedPostIds.includes(featuredPostId));
    setFormattedFeaturedPosts(formatFeaturedPostsForSinglePost(filteredFeaturedPosts));
  }, [featuredPosts, dismissedFeaturedPostIds]);

  return formattedFeaturedPosts;
};

const useHUStickyCampaign = () => {
  const dispatch = useDispatch();
  const featuredPosts = useSelector(({ context }: State) => context.currentCommunity?.featuredPosts);
  const dismissedFeaturedPostIds = useSelector(({ context }: State) => context.currentCommunity?.dismissedFeaturedPostIds) || [];
  const { currentUser, isFloatingSignupBoxVisible } = useSelector(({ context }: State) => context);
  const [firstHUCampaign, setFirstHUCampaign] = useState<Featured | undefined>(undefined);

  useEffect(() => {
    if (!featuredPosts || featuredPosts.length === 0) return;
    setFirstHUCampaign(featuredPosts?.find(({featuredByCommunityName, featuredPostId}) =>
      featuredByCommunityName === 'HealthUnlocked' && !dismissedFeaturedPostIds.includes(featuredPostId)));
  }, [featuredPosts, dismissedFeaturedPostIds]);

  useEffect(() => {
    // If we have an HU sticky campaign to show, we hide FloatingSignupBox for visitors on the single post page
    if (firstHUCampaign && isFloatingSignupBoxVisible) {
      dispatch(hideFloatingSignupBox());
    }
  }, [firstHUCampaign, isFloatingSignupBoxVisible]);

  const showCampaign = !currentUser && firstHUCampaign;

  return showCampaign ? firstHUCampaign : null;
};
